import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import { copyToClipboard } from 'utils/helpers/copyToClipboard';
import type { BusinessModeFieldType, BusinessModeType } from '../../types';
import { BUSINESS_MODE_PERSIST_FIELDS } from './constants';
import { normalizeModeFields } from './normalizeModeFields';

const useBusinessMode = () => {
    const { t } = useTranslation(['Settings', 'common']);
    const { showToast } = useToast();
    const { settings, localSettings, dispatchLocalSettings, saveSettings } = useSettings();
    const [isSaving, setIsSaving] = useState(false);
    const [showCustomEditor, setShowCustomEditor] = useState(false);
    const [customEditorTab, setCustomEditorTab] = useState<'form' | 'help'>('form');
    const [customEditorValue, setCustomEditorValue] = useState('');

    const selectedMode = (localSettings?.mode || 'general') as BusinessModeType;
    const modeForms = localSettings?.DIRS?.ModeForms || [];
    const activeFields = Array.isArray(localSettings?.modeFields) ? localSettings.modeFields : [];

    const modeDefaultFields = useMemo(
        () =>
            new Map<BusinessModeType, BusinessModeFieldType[]>(
                modeForms.map((item: { mode: BusinessModeType; fields: BusinessModeFieldType[] }) => [
                    item.mode,
                    item.fields || [],
                ]),
            ),
        [modeForms],
    );

    const getDefaultModeFields = (mode: BusinessModeType): BusinessModeFieldType[] => {
        if (mode === 'general') return [];
        if (mode === 'custom') {
            const customModeFields = localSettings?.customModeFields;
            return Array.isArray(customModeFields) ? customModeFields : [];
        }

        return modeDefaultFields.get(mode) || [];
    };

    const hasUnsavedChanges = () =>
        BUSINESS_MODE_PERSIST_FIELDS.some(
            (field) => JSON.stringify(settings?.[field]) !== JSON.stringify(localSettings?.[field]),
        );

    const persistSettingsIfChanged = async () => {
        if (!hasUnsavedChanges()) return;
        await saveSettings();
    };

    const handleSelectMode = async (mode: BusinessModeType) => {
        if (mode === selectedMode || isSaving) return;

        setIsSaving(true);
        try {
            await persistSettingsIfChanged();
            const modeFields = getDefaultModeFields(mode);
            dispatchLocalSettings({ type: 'SET_FIELD', field: 'mode', value: mode });
            dispatchLocalSettings({ type: 'SET_FIELD', field: 'modeFields', value: modeFields });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveMode = async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            await saveSettings();
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenCustomEditor = () => {
        if (selectedMode !== 'custom') {
            const customFields = getDefaultModeFields('custom');
            dispatchLocalSettings({ type: 'SET_FIELD', field: 'mode', value: 'custom' });
            dispatchLocalSettings({ type: 'SET_FIELD', field: 'modeFields', value: customFields });
        }
        const customModeFields = Array.isArray(localSettings?.customModeFields) ? localSettings.customModeFields : [];
        setCustomEditorValue(JSON.stringify(customModeFields, null, 2));
        setCustomEditorTab('form');
        setShowCustomEditor(true);
    };

    const handleApplyCustomEditor = () => {
        let parsed: unknown = [];
        try {
            parsed = JSON.parse(customEditorValue || '[]');
        } catch (_e) {
            showToast(t('businessMode.editor.invalidJson'), 'danger');
            return;
        }

        const normalizedFields = normalizeModeFields(parsed);
        dispatchLocalSettings({ type: 'SET_FIELD', field: 'customModeFields', value: normalizedFields });
        if (selectedMode === 'custom') {
            dispatchLocalSettings({ type: 'SET_FIELD', field: 'modeFields', value: normalizedFields });
        }
        setShowCustomEditor(false);
        showToast(t('businessMode.editor.saved'), 'success');
    };

    const handleCopyActiveStructure = async () => {
        if (!activeFields.length) return;
        const copied = await copyToClipboard(JSON.stringify(activeFields, null, 2));
        showToast(
            copied ? t('businessMode.structureCopied') : t('businessMode.structureCopyFailed'),
            copied ? 'success' : 'danger',
        );
    };

    return {
        selectedMode,
        activeFields,
        isSaving,
        showCustomEditor,
        setShowCustomEditor,
        customEditorTab,
        setCustomEditorTab,
        customEditorValue,
        setCustomEditorValue,
        handleSelectMode,
        handleSaveMode,
        handleOpenCustomEditor,
        handleApplyCustomEditor,
        handleCopyActiveStructure,
    };
};

export default useBusinessMode;
